import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../data/ColisContext';
import { BRAND } from '../data/constants';
import { inputStyle, focusInput, blurInput } from '../utils/helpers';

export default function ModalEditColisAdmin({ colisId, onClose }) {
  const { user, adminColis, refreshAdminColis, showToast } = useApp();
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  const pkg = useMemo(() => {
    if (!colisId) return null;
    return (adminColis || []).find(c => c?.id === colisId) || null;
  }, [adminColis, colisId]);

  const [form, setForm] = useState({
    nom: '',
    tel: '',
    destinataire: '',
    adresse: '',
    zone: '',
  });

  useEffect(() => {
    if (!pkg) return;
    setForm({
      nom: pkg?.nom || '',
      tel: pkg?.tel || '',
      destinataire: pkg?.destinataire || '',
      adresse: pkg?.adresse || '',
      zone: pkg?.zone || '',
    });
    setPhotoFile(null);
  }, [pkg]);

  if (!user?.is_admin || !colisId || !pkg) return null;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      let photoBase64 = '';
      if (photoFile) {
        photoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error('Impossible de lire la photo'));
          reader.onload = () => resolve(String(reader.result || ''));
          reader.readAsDataURL(photoFile);
        });
      }

      const res = await fetch(`/api/index.php?__path__=${encodeURIComponent(`admin/colis/${colisId}`)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          nom: form.nom,
          tel: form.tel,
          destinataire: form.destinataire,
          adresse: form.adresse,
          zone: form.zone,
          ...(photoBase64 ? { photo_base64: photoBase64, photo_name: photoFile?.name || '' } : {}),
        }),
      });

      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch { json = null; }
      if (!res.ok) {
        const msg = json?.message || text || 'Erreur serveur';
        throw new Error(String(msg));
      }

      await refreshAdminColis();
      showToast('Colis modifié ✅', 'success');
      onClose();
    } catch (e) {
      showToast(e?.message || 'Erreur', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleKey}
    >
      <div style={{
        background: 'white',
        borderRadius: 20,
        padding: 28,
        width: '100%',
        maxWidth: 520,
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
        animation: 'fadeIn 0.2s ease-out',
      }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#1e293b', marginBottom: 6 }}>
          ✏️ Modifier colis
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
          Colis <strong style={{ color: BRAND.primary }}>{pkg.id}</strong>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Field label="Nom" value={form.nom} onChange={set('nom')} />
          <Field label="Téléphone" value={form.tel} onChange={set('tel')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Field label="Destinataire" value={form.destinataire} onChange={set('destinataire')} />
          <Field label="Zone" value={form.zone} onChange={set('zone')} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
            Adresse
          </label>
          <input
            value={form.adresse}
            onChange={set('adresse')}
            style={inputStyle}
            onFocus={focusInput}
            onBlur={blurInput}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
            Photo (optionnel)
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
          />
          {pkg?.photo && (
            <div style={{ marginTop: 10 }}>
              <a href={pkg.photo} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 900, color: '#4F46E5', textDecoration: 'none' }}>
                Voir la photo actuelle
              </a>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              padding: '13px',
              background: saving ? '#e2e8f0' : BRAND.gradient,
              color: saving ? '#94a3b8' : 'white',
              border: 'none',
              borderRadius: 12,
              fontWeight: 900,
              fontSize: 14,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Enregistrement...' : '✅ Enregistrer'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '13px 20px',
              background: '#f1f5f9',
              color: '#475569',
              border: 'none',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
        {label}
      </label>
      <input
        value={value}
        onChange={onChange}
        style={inputStyle}
        onFocus={focusInput}
        onBlur={blurInput}
      />
    </div>
  );
}
